/**
 * The reports this person can reach, and which one is open.
 *
 * The list and the open report are kept apart on purpose. A list entry carries
 * no document, so listing forty reports does not pull forty documents down;
 * opening one fetches it. Conflating them is how a list view ends up loading
 * megabytes to draw a row of names.
 *
 * Nothing here filters by access. The server decides what this person may see,
 * and a filter applied in a browser is a filter an attacker can remove.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as api from '@/api/drafts'
import { ApiError, NetworkError } from '@/api/http'
import type { DraftFull, DraftSummary, TransitionRequest } from '@/domain/draft'
import type { JsonObject } from '@/domain/json'
import { mayEditContent, type Mover } from '@/domain/workflow'
import { t } from '@/i18n'
import { useSessionStore } from '@/stores/session'

export const useDraftsStore = defineStore('drafts', () => {
  const session = useSessionStore()

  const summaries = ref<readonly DraftSummary[]>([])
  const open = ref<DraftFull | null>(null)

  const isLoadingList = ref(false)
  const isLoadingDraft = ref(false)
  const isSaving = ref(false)
  const problem = ref<string | null>(null)

  /** Reports this person owns. */
  const mine = computed(() => summaries.value.filter((draft) => draft.myRole === 'owner'))

  /** Reports somebody shared with them. */
  const shared = computed(() => summaries.value.filter((draft) => draft.myRole !== 'owner'))

  /** Invitations still waiting to be accepted. */
  const invitations = computed(() =>
    summaries.value.filter((draft) => draft.myRole !== null && draft.myAcceptedAt === null),
  )

  /** How the person signed in relates to the open report. */
  const mover = computed<Mover>(() => ({
    isOwner: open.value?.myRole === 'owner',
    roles: session.roles,
    isDraftApprover: open.value?.myRole === 'validateur' && open.value.myAcceptedAt !== null,
  }))

  /** Whether the open report can be edited right now, by this person. */
  const canEdit = computed(() => {
    const draft = open.value
    if (!draft) return false
    return mayEditContent(draft.myRole, draft.state)
  })

  /**
   * A failure, in words a person can act on.
   *
   * The API's own message is preferred, because it knows what went wrong.
   * Everything else falls back to the catalogue: an exception's `message` is
   * written for a developer and is always in English.
   */
  function describe(error: unknown): string {
    if (error instanceof ApiError) return error.message
    if (error instanceof NetworkError) return t('common.unreachable')
    return t('common.problem')
  }

  async function loadList(): Promise<void> {
    isLoadingList.value = true
    problem.value = null
    try {
      summaries.value = await api.listDrafts()
    } catch (error) {
      problem.value = describe(error)
    } finally {
      isLoadingList.value = false
    }
  }

  async function openDraft(id: number): Promise<DraftFull | null> {
    isLoadingDraft.value = true
    problem.value = null
    try {
      open.value = await api.loadDraft(id)
      return open.value
    } catch (error) {
      problem.value = describe(error)
      open.value = null
      return null
    } finally {
      isLoadingDraft.value = false
    }
  }

  function close(): void {
    open.value = null
  }

  async function create(name: string, payload: JsonObject = {}): Promise<DraftFull | null> {
    problem.value = null
    try {
      const created = await api.createDraft({ name, payload })
      open.value = created
      // Cheaper and more accurate than refetching: the server has just told
      // us exactly what the new row looks like.
      summaries.value = [created, ...summaries.value]
      return created
    } catch (error) {
      problem.value = describe(error)
      return null
    }
  }

  /**
   * Save the open report.
   *
   * Returns whether it worked, because the caller usually needs to know: an
   * autosave that failed silently is how an afternoon's work disappears.
   */
  async function save(changes: { name?: string; payload?: JsonObject }): Promise<boolean> {
    const draft = open.value
    if (!draft) return false

    isSaving.value = true
    problem.value = null
    try {
      const saved = await api.saveDraft(draft.id, changes)
      open.value = saved
      replaceSummary(saved)
      return true
    } catch (error) {
      problem.value = describe(error)
      return false
    } finally {
      isSaving.value = false
    }
  }

  async function remove(id: number): Promise<boolean> {
    problem.value = null
    try {
      await api.deleteDraft(id)
      summaries.value = summaries.value.filter((draft) => draft.id !== id)
      if (open.value?.id === id) open.value = null
      return true
    } catch (error) {
      problem.value = describe(error)
      return false
    }
  }

  /** Move the open report through the workflow. */
  async function transition(request: TransitionRequest): Promise<boolean> {
    const draft = open.value
    if (!draft) return false

    problem.value = null
    try {
      const moved = await api.transitionDraft(draft.id, request)
      open.value = moved
      replaceSummary(moved)
      return true
    } catch (error) {
      problem.value = describe(error)
      return false
    }
  }

  /**
   * Carry a report into the next period, as a new report: the open one unless
   * another is named, which is how the new-report dialog starts from last year's.
   */
  async function rollForward(id?: number): Promise<DraftFull | null> {
    const from = id ?? open.value?.id
    if (from === undefined) return null

    problem.value = null
    try {
      const rolled = await api.rollForward(from)
      summaries.value = [rolled, ...summaries.value]
      return rolled
    } catch (error) {
      problem.value = describe(error)
      return null
    }
  }

  function replaceSummary(draft: DraftSummary): void {
    summaries.value = summaries.value.map((entry) => (entry.id === draft.id ? draft : entry))
  }

  /**
   * Note that somebody else saved the open report.
   *
   * Called when the live channel says so. The document is not refetched here:
   * replacing what somebody is editing with somebody else's version would
   * throw away their unsaved work. Whoever is watching decides.
   */
  const changedElsewhere = ref<{ by: string; at: string | null } | null>(null)

  function noticeRemoteChange(by: string, at: string | null): void {
    if (!open.value) return
    if (session.owns(by)) return
    changedElsewhere.value = { by, at }
  }

  /** Take the other person's version, discarding anything unsaved here. */
  async function reload(): Promise<void> {
    const draft = open.value
    if (!draft) return
    changedElsewhere.value = null
    await openDraft(draft.id)
  }

  function dismissRemoteChange(): void {
    changedElsewhere.value = null
  }

  return {
    summaries,
    open,
    isLoadingList,
    isLoadingDraft,
    isSaving,
    problem,
    mine,
    shared,
    invitations,
    mover,
    canEdit,
    changedElsewhere,
    loadList,
    openDraft,
    close,
    create,
    save,
    remove,
    transition,
    rollForward,
    noticeRemoteChange,
    reload,
    dismissRemoteChange,
  }
})
